import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import LanguageToggle from "@/components/LanguageToggle";

export default function Header() {
    const [open, setOpen] = useState(false);
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const { t } = useTranslation();

    const NAV = [
        { to: "/tools/legal-notice-generator", label: t("nav.legal_notice") },
        { to: "/tools/complaint-letter-generator", label: t("nav.complaint_letter") },
        { to: "/tools/case-checker", label: t("nav.case_checker") },
        { to: "/companies", label: t("nav.companies") },
        { to: "/consumer-forums", label: t("nav.forums") },
        { to: "/blog", label: t("nav.guides") },
    ];

    return (
        <header className="border-b border-[var(--np-border)] bg-[var(--np-bg)] sticky top-0 z-40" data-testid="site-header">
            <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between h-[68px]">
                <Link to="/" className="flex items-center gap-2.5" data-testid="logo-link">
                    <img src="/logo-mark.svg" alt="FileComplain" width="36" height="36" className="h-9 w-9 shrink-0" />
                    <div className="leading-none">
                        <div className="font-serif text-[22px] font-medium text-[var(--np-text)] tracking-tight">FileComplain</div>
                        <div className="np-label" style={{ fontSize: "0.6rem", letterSpacing: "0.18em" }}>Dispute Resolution, Simplified</div>
                    </div>
                </Link>

                <nav className="hidden lg:flex items-center gap-7">
                    {NAV.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className={({ isActive }) =>
                                `text-[14px] transition-colors ${isActive ? "text-[var(--np-accent)]" : "text-[var(--np-text-2)] hover:text-[var(--np-text)]"}`
                            }
                        >
                            {n.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <LanguageToggle />
                    {user ? (
                        <>
                            <Link
                                to={user.role === "admin" ? "/admin" : "/dashboard"}
                                className="hidden sm:inline-block text-sm text-[var(--np-text-2)] hover:text-[var(--np-text)] transition-colors"
                                data-testid="nav-dashboard"
                            >
                                {user.role === "admin" ? "Admin" : t("nav.dashboard")}
                            </Link>
                            <button
                                onClick={async () => { await logout(); nav("/"); }}
                                className="hidden sm:inline-block text-sm text-[var(--np-text-2)] hover:text-[var(--np-text)]"
                                data-testid="logout-btn"
                            >
                                {t("nav.logout")}
                            </button>
                        </>
                    ) : (
                        <>
                            <Link to="/login" className="hidden sm:inline-block text-sm text-[var(--np-text-2)] hover:text-[var(--np-text)]" data-testid="header-login-link">
                                {t("nav.login")}
                            </Link>
                            <Link to="/contact" className="np-btn-primary hidden sm:inline-flex text-sm !py-2.5 !px-4" data-testid="header-talk-to-expert">
                                {t("nav.talk_to_expert")}
                            </Link>
                        </>
                    )}
                    <button onClick={() => setOpen(!open)} className="lg:hidden p-2 -mr-2" aria-label="Menu" data-testid="mobile-menu-toggle">
                        {open ? <X size={22} /> : <Menu size={22} />}
                    </button>
                </div>
            </div>

            {open && (
                <div className="lg:hidden border-t border-[var(--np-border)] bg-[var(--np-bg-secondary)]">
                    <div className="max-w-7xl mx-auto px-5 py-4 flex flex-col gap-1">
                        {NAV.map((n) => (
                            <Link key={n.to} to={n.to} onClick={() => setOpen(false)}
                                className="py-2.5 text-[15px] text-[var(--np-text-2)]"
                                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>
                                {n.label}
                            </Link>
                        ))}
                        <div className="border-t border-[var(--np-border)] mt-2 pt-3 flex flex-col gap-2">
                            {user ? (
                                <>
                                    <Link to={user.role === "admin" ? "/admin" : "/dashboard"} className="py-2 text-[15px]" onClick={() => setOpen(false)}>
                                        {user.role === "admin" ? "Admin" : "Dashboard"}
                                    </Link>
                                    <button onClick={async () => { setOpen(false); await logout(); nav("/"); }}
                                        className="py-2 text-left text-[15px]">Logout</button>
                                </>
                            ) : (
                                <>
                                    <Link to="/login" onClick={() => setOpen(false)} className="py-2 text-[15px]">Login</Link>
                                    <Link to="/contact" onClick={() => setOpen(false)} className="np-btn-primary text-sm w-full justify-center">Talk to Expert</Link>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
}
